import React, { useEffect, useMemo } from "react";
import { Menu } from "antd";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    NotificationOutlined,
} from "@ant-design/icons";

import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import UserProfile from "./UserProfile";
import IntlMessages from "util/IntlMessages";
import { fetchMaintenanceDepartments } from "appRedux/actions/MaintenanceActions";
import {
    NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
    THEME_TYPE_LITE,
} from "constants/ThemeSetting";
import {
    hasAdminAccess,
    hasAllAccess,
    hasMaintenanceAdminAccess,
    hasMarketingAccess,
    hasRootAccess,
    hasMaintenanceAccess
} from "util/Utils";
import {
    getTmsMenus,
    getMarketingMenus,
    getPushMenu,
    getMaintenanceMenus,
    getAppServiceMenus,
    getSettingsMenus,
    getOleMenus,
} from "./menuConfig";

const { SubMenu } = Menu;

const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed }) => {
    const dispatch = useDispatch();
    const { navStyle, themeType } = useSelector(({ settings }) => settings);
    const { authUser } = useSelector(({ auth }) => auth);
    const { pathname } = useSelector(({ common }) => common);
    const { maintenanceDepartments } = useSelector(state => state.maintenanceReducer);

    const selectedKeys = pathname.substr(1);
    const defaultOpenKeys = selectedKeys.split("/")[1];

    const isAll = hasAllAccess(authUser.role);
    const isAdmin = hasAdminAccess(authUser.role);
    const isMarketing = hasMarketingAccess(authUser.role);
    const isRoot = hasRootAccess(authUser.role);
    const isMainAdmin = hasMaintenanceAdminAccess(authUser.role);
    const isMaintenance = hasMaintenanceAccess(authUser.role);
    const isStudentBuilding = authUser?.assigned_building_types?.includes("STUDENT");

    useEffect(() => {
        if (maintenanceDepartments.length === 0 && isMainAdmin) {
            dispatch(fetchMaintenanceDepartments(true));
        }
    }, [maintenanceDepartments.length, isMainAdmin, dispatch]);

    const getSubMenuClass = () =>
        navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR ? "gx-no-header-submenu-popup" : "";

    const menuTheme = themeType === THEME_TYPE_LITE ? "lite" : "dark";

    const allMenus = useMemo(() => (
        [
            ...getTmsMenus({ isAll, isAdmin, isStudentBuilding }),
            ...getMarketingMenus({ isAll, isMarketing }),
            ...getPushMenu({ isAll }),
            ...getAppServiceMenus({ isAll, isRoot }),
            ...getSettingsMenus({ isAll, isRoot }),
            ...getMaintenanceMenus({ isAdmin, isMainAdmin, hasMaintenanceAccess: isMaintenance, isAll, maintenanceDepartments }),
            ...getOleMenus(isAll, isStudentBuilding)
        ]
    ), [authUser.role, maintenanceDepartments, isAll, isAdmin, isMarketing, isRoot, isMainAdmin]);

    const renderMenuItem = (item) => {
        if (item.type === "submenu") {
            return (
                <SubMenu
                    key={item.key}
                    popupClassName={getSubMenuClass()}
                    title={
                        <span>
              {item.icon === NotificationOutlined ? <NotificationOutlined /> : <i className={`icon ${item.icon}`} />}
                            <span><IntlMessages id={item.label} /></span>
            </span>
                    }
                >
                    {item.children.map(renderMenuItem)}
                </SubMenu>
            );
        }
        const IconComp = item.icon === NotificationOutlined || typeof item.icon === 'function'
            ? item.icon
            : () => <i className={`icon ${item.icon}`} />;

        return (
            <Menu.Item key={item.key} onClick={item.onClick}>
                <Link to={item.to}>
                    <IconComp />
                    <span>
            {item.label.startsWith("sidebar.") ? <IntlMessages id={item.label} /> : item.label}
          </span>
                </Link>
            </Menu.Item>
        );
    };

    return (
        <>
            <SidebarLogo sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} />
            <div className="gx-sidebar-content">
                <div className="gx-sidebar-notifications">
                    <UserProfile />
                </div>
                <CustomScrollbars className="gx-layout-sider-scrollbar">
                    <Menu
                        defaultOpenKeys={[defaultOpenKeys]}
                        selectedKeys={[selectedKeys]}
                        theme={menuTheme}
                        mode="inline"
                    >
                        {allMenus.map(renderMenuItem)}
                    </Menu>
                </CustomScrollbars>
            </div>
        </>
    );
};

export default SidebarContent;
